
import { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';
import { Language, Message } from '../types';

/**
 * Manual base64 encoding/decoding as per Live API requirements.
 */
function encode(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decode(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Custom raw PCM audio decoder for Live API streams.
 */
async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

interface UseLiveAssistantProps {
  language: Language;
  history: Message[];
  userName: string;
  onMessageReceived: (role: 'user' | 'assistant', text: string) => void;
  onError: (error: string) => void;
}

export const useLiveAssistant = ({ language, history, userName, onMessageReceived, onError }: UseLiveAssistantProps) => {
  const [isActive, setIsActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [speakerLevel, setSpeakerLevel] = useState(0);
  
  const sessionRef = useRef<any>(null);
  const audioContextsRef = useRef<{ input: AudioContext; output: AudioContext } | null>(null);
  const audioNodesRef = useRef<{ source: MediaStreamAudioSourceNode; processor: ScriptProcessorNode } | null>(null);
  const playbackSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const nextStartTimeRef = useRef<number>(0);
  
  const inputAnalyserRef = useRef<AnalyserNode | null>(null);
  const outputAnalyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  
  const currentInputTranscription = useRef('');
  const currentOutputTranscription = useRef('');
  const isClosingRef = useRef(false);

  const stopSession = useCallback(() => {
    isClosingRef.current = true;
    if (sessionRef.current) {
      try { sessionRef.current.close(); } catch (e) {}
      sessionRef.current = null;
    }
    
    if (audioNodesRef.current) {
      try {
        audioNodesRef.current.source.disconnect();
        audioNodesRef.current.processor.disconnect();
      } catch (e) {}
      audioNodesRef.current = null;
    }

    playbackSourcesRef.current.forEach(s => {
      try { s.stop(); } catch (e) {}
    });
    playbackSourcesRef.current.clear();
    
    setIsActive(false);
    setIsSpeaking(false);
    setMicLevel(0);
    setSpeakerLevel(0);
  }, []);

  const startSession = useCallback(async () => {
    isClosingRef.current = false;
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      
      if (!audioContextsRef.current) {
        audioContextsRef.current = {
          input: new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 }),
          output: new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 })
        };
      }

      const { input: inputCtx, output: outputCtx } = audioContextsRef.current;
      if (inputCtx.state === 'suspended') await inputCtx.resume();
      if (outputCtx.state === 'suspended') await outputCtx.resume();

      const inputAnalyser = inputCtx.createAnalyser();
      inputAnalyser.fftSize = 64;
      inputAnalyserRef.current = inputAnalyser;

      const outputAnalyser = outputCtx.createAnalyser();
      outputAnalyser.fftSize = 64;
      outputAnalyserRef.current = outputAnalyser;

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true 
      });

      const historyContext = history.length > 0 
        ? `\n\nContexto da conversa atual (Memória):\n${history.map(m => `${m.role === 'user' ? 'Utilizador' : 'BABA'}: ${m.text}`).join('\n')}`
        : '';

      const languageNote = language === Language.EN_GB 
        ? "STRICTLY use British English (en-GB). British spelling and terms only. No Americanisms."
        : "STRICTLY use European Portuguese (pt-PT). Use Portugal's vocabulary and grammar (e.g., 'tu', 'ecrã', 'telemóvel'). Include regional expressions from Portugal when appropriate. No Brazilian Portuguese.";

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        callbacks: {
          onopen: () => {
            setIsActive(true);
            const source = inputCtx.createMediaStreamSource(stream);
            const scriptProcessor = inputCtx.createScriptProcessor(4096, 1, 1);
            
            scriptProcessor.onaudioprocess = (e) => {
              const inputData = e.inputBuffer.getChannelData(0);
              const int16 = new Int16Array(inputData.length);
              for (let i = 0; i < inputData.length; i++) {
                int16[i] = inputData[i] * 32768;
              }
              const pcmBlob = {
                data: encode(new Uint8Array(int16.buffer)),
                mimeType: 'audio/pcm;rate=16000',
              };
              
              sessionPromise.then(session => {
                session.sendRealtimeInput({ media: pcmBlob });
              }).catch(err => {
                console.error("[LiveAssistant] Failed to send audio:", err);
              });
            };

            source.connect(inputAnalyser);
            source.connect(scriptProcessor);
            scriptProcessor.connect(inputCtx.destination);
            audioNodesRef.current = { source, processor: scriptProcessor };
          },
          onmessage: async (message: LiveServerMessage) => {
            if (message.serverContent) {
              if (message.serverContent.inputTranscription) {
                currentInputTranscription.current += message.serverContent.inputTranscription.text;
              }
              if (message.serverContent.outputTranscription) {
                currentOutputTranscription.current += message.serverContent.outputTranscription.text;
              }

              if (message.serverContent.turnComplete) {
                const uText = currentInputTranscription.current.trim();
                const aText = currentOutputTranscription.current.trim();
                if (uText) onMessageReceived('user', uText);
                if (aText) onMessageReceived('assistant', aText);
                currentInputTranscription.current = '';
                currentOutputTranscription.current = '';
              }

              const parts = message.serverContent.modelTurn?.parts || [];
              for (const part of parts) {
                if (part.inlineData?.data) {
                  setIsSpeaking(true);
                  nextStartTimeRef.current = Math.max(nextStartTimeRef.current, outputCtx.currentTime);
                  try {
                    const buffer = await decodeAudioData(decode(part.inlineData.data), outputCtx, 24000, 1);
                    const source = outputCtx.createBufferSource();
                    source.buffer = buffer;
                    source.connect(outputAnalyser);
                    outputAnalyser.connect(outputCtx.destination);
                    source.addEventListener('ended', () => {
                      playbackSourcesRef.current.delete(source);
                      if (playbackSourcesRef.current.size === 0) setIsSpeaking(false);
                    });
                    source.start(nextStartTimeRef.current);
                    nextStartTimeRef.current += buffer.duration;
                    playbackSourcesRef.current.add(source);
                  } catch (err) {
                    console.error("[LiveAssistant] Audio decoding error:", err);
                  }
                }
              }

              if (message.serverContent.interrupted) {
                playbackSourcesRef.current.forEach(s => { try { s.stop(); } catch (e) {} });
                playbackSourcesRef.current.clear();
                nextStartTimeRef.current = 0;
                setIsSpeaking(false);
              }
            }
          },
          onerror: (e: any) => {
            onError("Erro na interação de voz.");
            stopSession();
          },
          onclose: (e) => {
            setIsActive(false);
            if (!isClosingRef.current) stopSession();
          }
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Charon' } },
          },
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          systemInstruction: `És o BABA, um assistente de IA bilingue. Utilizador: ${userName}. ${languageNote} Responde de forma natural e concisa.${historyContext}`,
        }
      });

      sessionRef.current = await sessionPromise;
    } catch (err: any) {
      console.error("[LiveAssistant] Initialization failure:", err);
      onError("Falha ao iniciar a sessão de voz.");
      setIsActive(false);
    }
  }, [language, history, userName, onMessageReceived, onError, stopSession]);

  useEffect(() => {
    let active = true;
    const inputDataArray = new Uint8Array(32);
    const outputDataArray = new Uint8Array(32);

    const updateLevels = () => {
      if (!active) return;

      if (isActive && inputAnalyserRef.current) {
        inputAnalyserRef.current.getByteFrequencyData(inputDataArray);
        let sum = 0;
        for (let i = 0; i < inputDataArray.length; i++) {
          sum += inputDataArray[i];
        }
        const avg = sum / inputDataArray.length;
        setMicLevel(avg / 128); // 0 to ~1
      } else {
        setMicLevel(0);
      }

      if (isActive && outputAnalyserRef.current) {
        outputAnalyserRef.current.getByteFrequencyData(outputDataArray);
        let sum = 0;
        for (let i = 0; i < outputDataArray.length; i++) {
          sum += outputDataArray[i];
        }
        const avg = sum / outputDataArray.length;
        setSpeakerLevel(avg / 128); // 0 to ~1
      } else {
        setSpeakerLevel(0);
      }

      animationFrameIdRef.current = requestAnimationFrame(updateLevels);
    };

    if (isActive) {
      animationFrameIdRef.current = requestAnimationFrame(updateLevels);
    } else {
      setMicLevel(0);
      setSpeakerLevel(0);
    }

    return () => {
      active = false;
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [isActive]);

  useEffect(() => {
    return () => {
      isClosingRef.current = true;
      stopSession();
    };
  }, [stopSession]);

  return { isActive, isSpeaking, startSession, stopSession, micLevel, speakerLevel };
};
